import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, KeyRound, LoaderCircle, UserRoundX } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIsUnlocked, type ScopeInfo } from "./keyring";
import { accessQuery, shareScopeKey, useFingerprint, useScopeAccess, type AccessMember } from "./personal";

function Fingerprint({ publicKey }: { publicKey: string | null }) {
  const fp = useFingerprint(publicKey);
  if (!publicKey) return null;
  return (
    <span className="block font-mono text-[0.68rem] text-muted-foreground" title="Fingerprint of their personal key. Compare it with them to be sure the key is theirs.">
      {fp.data ?? "…"}
    </span>
  );
}

/**
 * Who can open an encrypted notebook, project or note: members it was shared with, members
 * waiting for access, and members without a personal key yet. Anyone who has the key unlocked
 * can give waiting members access (the key is wrapped for their personal key in this browser).
 */
export function ScopeAccess({ scope, what }: { scope: Pick<ScopeInfo, "id" | "slots">; what: string }) {
  const qc = useQueryClient();
  const me = useMe();
  const access = useScopeAccess(scope.id);
  const unlocked = useIsUnlocked(scope.id);
  const [busy, setBusy] = useState<string | null>(null);
  const hasPassphrase = scope.slots.some((s) => s.kind === "passphrase");

  const give = async (members: AccessMember[]) => {
    setBusy(members.length === 1 ? members[0].id : "all");
    try {
      const done = await shareScopeKey(scope.id, members);
      await qc.invalidateQueries({ queryKey: accessQuery(scope.id) });
      if (done.length) toast.success(`${members.filter((m) => done.includes(m.id)).map((m) => m.displayName).join(", ")} can now open this ${what}.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (access.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (access.isError) return <p className="text-sm text-destructive">{errorMessage(access.error)}</p>;
  const members = access.data ?? [];
  const waiting = members.filter((m) => !m.hasKey && m.publicKey);

  return (
    <div className="space-y-2 text-sm">
      <ul className="divide-y rounded-md border">
        {members.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="min-w-0 flex-1">
              {m.displayName}
              {m.id === me.data?.id && <span className="text-muted-foreground"> (you)</span>}
              <Fingerprint publicKey={m.publicKey} />
            </span>
            {m.hasKey ? (
              <Badge variant="outline" className="gap-1 border-primary/40 font-normal text-primary">
                <Check className="size-3" aria-hidden /> Can open it
              </Badge>
            ) : m.publicKey ? (
              <>
                <Badge variant="outline" className="font-normal">
                  Not shared yet
                </Badge>
                <Button size="xs" variant="outline" disabled={!unlocked || !!busy} onClick={() => void give([m])} title={unlocked ? undefined : "Unlock it first."}>
                  {busy === m.id ? <LoaderCircle className="animate-spin" /> : <KeyRound />} Give access
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="gap-1 font-normal text-muted-foreground" title="They need to set up a personal key (Settings → Privacy & security → Your personal key) before the key can be shared with them.">
                <UserRoundX className="size-3" aria-hidden /> No personal key yet
              </Badge>
            )}
          </li>
        ))}
      </ul>
      {waiting.length > 1 && (
        <Button size="sm" variant="outline" disabled={!unlocked || !!busy} onClick={() => void give(waiting)}>
          {busy === "all" ? <LoaderCircle className="animate-spin" /> : <KeyRound />} Give access to all {waiting.length} waiting members
        </Button>
      )}
      <p className="text-xs text-muted-foreground">
        Giving access shares the key with that person securely, from your browser. BrainLog never sees it.
        {!unlocked && " Unlock it to give access."}
        {hasPassphrase && ` Anyone who knows this ${what}’s passphrase can open it too.`} Removing a member also withdraws the key shared with them, but cannot erase what they may already have
        copied.
      </p>
    </div>
  );
}

export function ScopeAccessDialog({ scope, what, open, onOpenChange }: { scope: Pick<ScopeInfo, "id" | "slots">; what: string; open: boolean; onOpenChange(o: boolean): void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-foreground sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Who can open this {what}</DialogTitle>
          <DialogDescription>Members who received the key open it with their own personal passphrase.</DialogDescription>
        </DialogHeader>
        <ScopeAccess scope={scope} what={what} />
      </DialogContent>
    </Dialog>
  );
}
